import type {
    MouseEventHandler,
    FC,
    RefObject,
    FocusEventHandler,
    ForwardedRef,
} from "react";
import React, {
    Children,
    cloneElement,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";

import { getDisplayName } from "../../../../../utils/react";

import type { TabProps } from "../../../Tab";
import type { TabViewsProps } from "../../types";

interface ITabsProps
    extends Pick<
        TabViewsProps,
        "children" | "selectedTabIdx" | "onChangeSelectedTabIdx" | "onTabFocus"
    > {
    tabsRef: RefObject<HTMLButtonElement[]>;
}

const Tabs: FC<ITabsProps> = ({
    children,
    selectedTabIdx,
    onChangeSelectedTabIdx,
    onTabFocus,
    tabsRef,
}) => {
    const ref = useRef<HTMLButtonElement[]>([]);

    const [focused, setFocused] = useState(false);
    const [clicked, setClicked] = useState(false);

    const focusedByKeyboard = focused && !clicked;

    useEffect(() => {
        ref.current = [];
    }, [children]);

    useImperativeHandle(tabsRef, () => ref.current);

    if (!children) {
        throw new Error(
            "В компонент TabsView не был передан ни один компонент",
        );
    }

    const getTabIndex = (index: number) => {
        const noTabIsSelected = selectedTabIdx === undefined;
        if (focusedByKeyboard || noTabIsSelected) {
            return 0;
        }
        const thisTabIsSelected = selectedTabIdx === index;
        return thisTabIsSelected ? 0 : -1;
    };

    return (
        <>
            {Children.map(children, (child, idx) => {
                if (getDisplayName(child) !== "TabsView.Tab") {
                    throw new Error(
                        "В компоненте TabsView могут быть только компоненты Tab",
                    );
                }

                const {
                    onClick: childOnClick,
                    onFocus: childOnFocus,
                    ...childProps
                } = child.props;

                const onClick: MouseEventHandler<HTMLButtonElement> = (evt) => {
                    setClicked(true);
                    childOnClick?.(evt);
                    onChangeSelectedTabIdx?.(idx);
                };

                const onFocus: FocusEventHandler<HTMLButtonElement> = (evt) => {
                    setFocused(true);
                    childOnFocus?.(evt);
                    onTabFocus?.(idx);
                };

                const onBlur: FocusEventHandler<HTMLButtonElement> = () => {
                    setFocused(false);
                };

                const tabWrapperRef = (childNode: HTMLButtonElement) => {
                    ref.current[idx] = childNode;
                };

                return cloneElement(child, {
                    ...childProps,
                    onClick,
                    onFocus,
                    onBlur,
                    selected: selectedTabIdx === idx,
                    tabIndex: getTabIndex(idx),
                    ref: tabWrapperRef,
                } as TabProps & {
                    ref: ForwardedRef<HTMLButtonElement>;
                });
            })}
        </>
    );
};

export default Tabs;
